import React, { RefObject, useState } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'

import styledWeb from 'styled-components'
import { useTheme } from 'styled-components/native'

import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { DragHandleIcon } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/icons/DragHandleIcon'
import { BodyTextSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { COMPACT_MESSENGER_Z_INDEX } from '../constants'
import { useDraggablePanel } from '../hooks/useDraggablePanel'

const VisuallyHidden = styledWeb.span({
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clipPath: 'inset(50%)',
  whiteSpace: 'nowrap',
})

// Room the tooltip needs above the handle: one line of small text, its padding and the gap.
const TOOLTIP_SPACE_ABOVE = 48

const Handle = styledWeb.button(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 28,
  padding: 0,
  border: 'none',
  borderRadius: theme.spacing['4px'],
  background: 'none',
  cursor: 'grab',
  // Pointer events drive the drag; keep touch input from scrolling the page instead.
  touchAction: 'none',
  '&:hover': { backgroundColor: theme.colors.tertiaryButtonBackgroundHover },
  '&[data-dragging]': { cursor: 'grabbing', backgroundColor: theme.colors.tertiaryButtonBackgroundPressed },
  '&:focus-visible': { outline: `2px solid ${theme.colors.borderFocus}`, outlineOffset: 1 },
}))

// Not the shared Tooltip: it expects a React Native Pressable trigger (it clones onHoverIn, onPress and hitSlop onto it,
// which this DOM button ignores) and its hideTooltip prop swaps the trigger's tree, remounting it mid-drag. This one
// sits outside the panel (so the panel's overflow does not clip it) and hides as soon as a move starts.
const DragTooltip = styledWeb.div<{ $left: number; $top: number; $below: boolean }>(
  ({ theme, $left, $top, $below }) => ({
    position: 'fixed',
    left: $left,
    top: $top,
    transform: $below ? `translateY(${theme.spacing['8px']})` : `translateY(calc(-100% - ${theme.spacing['8px']}))`,
    padding: `${theme.spacing['8px']} ${theme.spacing['12px']}`,
    borderRadius: theme.spacing['4px'],
    backgroundColor: theme.colors.backgroundTooltip,
    whiteSpace: 'nowrap',
    pointerEvents: 'none',
    zIndex: COMPACT_MESSENGER_Z_INDEX + 1,
  }),
)

export const CompactMessengerDragHandle = ({
  panelRef,
  testIDPrefix,
}: {
  panelRef: RefObject<HTMLElement | null>
  testIDPrefix: string
}) => {
  const { formatMessage } = useIntl()
  const theme = useTheme()
  const handleProps = useDraggablePanel(panelRef)
  const instructionsId = `${testIDPrefix}-drag-handle-instructions`
  const [tooltipAnchor, setTooltipAnchor] = useState<{ left: number; top: number; below: boolean } | null>(null)

  const showTooltip = (handle: HTMLElement) => {
    const rect = handle.getBoundingClientRect()
    // Flip below the handle when the panel is dragged against the top of the viewport.
    const below = rect.top < TOOLTIP_SPACE_ABOVE
    setTooltipAnchor({ left: rect.left, top: below ? rect.bottom : rect.top, below })
  }
  const hideTooltip = () => setTooltipAnchor(null)

  return (
    <>
      <Handle
        theme={theme}
        type='button'
        data-testid={tID(`${testIDPrefix}-drag-handle`)}
        aria-label={formatMessage({
          defaultMessage: 'Move messages panel',
          description: 'Accessibility label for the handle used to drag the compact messenger',
        })}
        aria-describedby={instructionsId}
        // Mouse only: the tooltip describes dragging, and keyboard users get the instructions below instead.
        onPointerEnter={(event) => event.pointerType === 'mouse' && showTooltip(event.currentTarget)}
        onPointerLeave={hideTooltip}
        onPointerDownCapture={hideTooltip}
        onKeyDownCapture={hideTooltip}
        {...handleProps}
      >
        <DragHandleIcon size={16} />
      </Handle>
      <VisuallyHidden id={instructionsId}>
        {formatMessage({
          defaultMessage: 'Use the arrow keys to move the panel. Hold Shift to move it further.',
          description: 'Screen reader instructions for moving the compact messenger with the keyboard',
        })}
      </VisuallyHidden>
      {tooltipAnchor &&
        createPortal(
          <DragTooltip
            theme={theme}
            $left={tooltipAnchor.left}
            $top={tooltipAnchor.top}
            $below={tooltipAnchor.below}
            aria-hidden
            data-testid={tID(`${testIDPrefix}-drag-handle-tooltip`)}
          >
            <BodyText
              text={formatMessage({
                defaultMessage: 'Drag to move',
                description: 'Tooltip on the handle used to drag the compact messenger around the page',
              })}
              size={BodyTextSize.SMALL}
              color={theme.colors.components?.tooltip?.text ?? theme.colors.textInverse}
            />
          </DragTooltip>,
          document.body,
        )}
    </>
  )
}
